import { useEffect, useState } from 'react'
import { FileText, History, ArrowLeft } from 'lucide-react'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { fetchCharterSession, fetchCharterVersions } from '../../redux/asyncThunks/charterThunks'
import {
  selectEffectiveRatifiedCharter,
  selectCharterSession,
  selectCharterVersions,
  selectCharterStatus,
  selectCharterVersion,
} from '../../redux/selectors/charterSelectors'
import { CharterViewer } from './CharterViewer'
import { CharterVersionHistory } from '../Renegotiation/CharterVersionHistory'
import { AmendmentProposalForm } from '../Renegotiation/AmendmentProposalModal'

interface CharterCardModalProps {
  isOpen: boolean
  onClose: () => void
  roomId: string
  isStudent?: boolean
  teamId?: number
}

export function CharterCardModal({ isOpen, onClose, roomId, isStudent = false, teamId: teamIdProp }: CharterCardModalProps) {
  const dispatch = useAppDispatch()

  const charter = useAppSelector(selectEffectiveRatifiedCharter)
  const session = useAppSelector(selectCharterSession)
  const versions = useAppSelector(selectCharterVersions)
  const charterStatus = useAppSelector(selectCharterStatus)
  const charterVersion = useAppSelector(selectCharterVersion)

  const status = charterStatus ?? session?.status
  const version = charterVersion ?? session?.version ?? 1
  const teamId = teamIdProp ?? session?.team_id ?? 0

  // "Propose Amendment" swaps this single dialog's content in-place instead of
  // opening a second (nested) Radix dialog — nesting suppressed the dialog until
  // a page reload. One dialog is ever mounted, so there's no nesting/focus race.
  const [mode, setMode] = useState<'view' | 'propose'>('view')

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchCharterSession({ roomId }))
      setMode('view')
    }
  }, [isOpen, dispatch, roomId])

  useEffect(() => {
    const tid = teamIdProp ?? session?.team_id
    if (isOpen && tid) {
      dispatch(fetchCharterVersions({ roomId, teamId: tid }))
    }
  }, [isOpen, dispatch, roomId, teamIdProp, session?.team_id])

  if (!charter) return null

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className='max-w-4xl w-full max-h-[90vh] overflow-y-auto'>
        {mode === 'propose' ? (
          <>
            <div className='mb-1'>
              <Button
                variant='ghost'
                size='sm'
                onClick={() => setMode('view')}
                className='gap-1.5 text-xs text-slate-500 hover:text-slate-800 -ml-2'
              >
                <ArrowLeft className='h-3.5 w-3.5' />
                Back to charter
              </Button>
            </div>
            <AmendmentProposalForm
              roomId={roomId}
              teamId={teamId}
              onCancel={() => setMode('view')}
              onSubmitted={() => {
                setMode('view')
                onClose()
              }}
            />
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className='text-base font-semibold text-slate-800'>
                Team Charter
              </DialogTitle>
            </DialogHeader>
            <Tabs defaultValue='charter'>
              <TabsList className='mb-4'>
                <TabsTrigger value='charter' className='gap-1.5 text-xs'>
                  <FileText className='h-3.5 w-3.5' />
                  Charter
                </TabsTrigger>
                <TabsTrigger value='history' className='gap-1.5 text-xs'>
                  <History className='h-3.5 w-3.5' />
                  Version History
                </TabsTrigger>
              </TabsList>
              <TabsContent value='charter'>
                <CharterViewer
                  charter={charter}
                  status={status ?? 'locked'}
                  version={version}
                  teamId={teamId}
                  isStudent={isStudent}
                  onProposeAmendment={() => setMode('propose')}
                />
              </TabsContent>
              <TabsContent value='history'>
                <CharterVersionHistory
                  versions={versions}
                  roomId={roomId}
                  teamId={teamId}
                  currentVersion={version}
                />
              </TabsContent>
            </Tabs>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
